import React, {useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {useTheme} from 'themes'
import {TreeFototoreeSimilarityResponse} from 'types'
import {getBorder} from 'utils'
import {
  Button,
  IconVerifiedBadge,
  Image,
  List,
  Modal,
  Paragraph,
} from 'common/components'
import convertUnit from 'lib/unit'
import {TreePlantSimilarityProps} from './TreePlantSimilarityProps'

interface StyledListContainerProps {
  height: number
}

interface StyledListItemContentNameProps {
  lineClamp?: number
}

const StyledModal = styled(Modal)`
  width: 100%;
  max-width: ${convertUnit(335)};
  padding: ${convertUnit(20)};
`

const StyledButtonContainer = styled.div`
  display: flex;
  margin-top: ${convertUnit(20)};
  gap: ${convertUnit(20)};
`
const StyledButton = styled(Button)`
  width: 100%;
`

const StyledTitle = styled(Paragraph)`
  text-align: center;
  margin-bottom: ${convertUnit(8)};
`

const StyledListItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertUnit(8)};
  padding: ${convertUnit(12)} 0;
`

const StyledImage = styled(Image)`
  object-fit: contain;
  width: ${convertUnit(24)};
  height: ${convertUnit(24)};
`

const StyledListContainer = styled.div<StyledListContainerProps>`
  display: flex;
  height: ${({height}) => convertUnit(height)};
  background-color: ${({theme}) => theme.white_3};
  border: ${({theme}) => getBorder(1, 'solid', theme.white_5)};
  border-radius: ${convertUnit(8)};
  margin-top: ${convertUnit(20)};
  box-sizing: border-box;
`

const StyledListItemContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledListItemContentNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertUnit(6)};
`

const StyledListItemContentName = styled(
  Paragraph,
)<StyledListItemContentNameProps>`
  display: -webkit-box;
  -webkit-line-clamp: ${({lineClamp = 1}) => lineClamp};
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export default function TreePlantSimilarity({
  loading,
  data,
  onCancel,
  onSubmit,
}: TreePlantSimilarityProps) {
  const theme = useTheme()
  const {translate} = useTranslation()

  const handleRenderItem = useCallback(
    (item: TreeFototoreeSimilarityResponse, index: number) => (
      <StyledListItemContainer
        style={{
          borderBottom: getBorder(
            data.length - 1 !== index ? 1 : 0,
            'solid',
            theme.white_5,
          ),
        }}>
        <StyledImage src={item.pinpoint_icon} alt={item.name} />
        <StyledListItemContentContainer>
          <StyledListItemContentNameContainer>
            <StyledListItemContentName fontSize="m" fontWeight="bold">
              {item.name}
            </StyledListItemContentName>
            {item.status === 'VERIFIED' && (
              <IconVerifiedBadge width={12} height={12} />
            )}
          </StyledListItemContentNameContainer>
          <StyledListItemContentName lineClamp={2}>
            {`${item.location.distance.toFixed()}M | ${item.location.name} | ${
              item.bio
            }`}
          </StyledListItemContentName>
        </StyledListItemContentContainer>
      </StyledListItemContainer>
    ),
    [data, theme.white_5],
  )

  const handleRenderList = useMemo(() => {
    const height = data.length >= 4 ? 328 : 82 * data.length
    return (
      <StyledListContainer height={height}>
        <List
          keyExtractor={(item) => item.id}
          data={data}
          renderItem={handleRenderItem}
          contentContainerStyle={{padding: `0 ${convertUnit(12)}`}}
        />
      </StyledListContainer>
    )
  }, [data, handleRenderItem])

  return (
    <StyledModal visible onBackdropPress={onCancel}>
      <StyledTitle fontSize="l" fontWeight="bold" lineHeight={31}>
        {translate('tree:similarityFototreeTitle')}
      </StyledTitle>
      <Paragraph fontSize="m" lineHeight={26}>
        {translate('tree:similarityFototreeDesc')}
      </Paragraph>
      {handleRenderList}
      <StyledButtonContainer>
        <StyledButton
          label={translate('global:cancel')}
          color="primary_5"
          backgroundColor="white_3"
          backgroundHoverColor="white_4"
          onClick={onCancel}
        />
        <StyledButton
          label={translate('tree:similarityFototreeButton')}
          isLoading={loading}
          onClick={onSubmit}
        />
      </StyledButtonContainer>
    </StyledModal>
  )
}
